import { memo, useCallback, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import {
	IconEyeSlashOutline18 as EyeOff,
	IconClockTimeOutline18 as History,
	IconLinkOutline18 as LinkIcon,
	IconAsteriskOutline18 as SquareAsterisk,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { Changelog, ChangelogTranslation } from "@productlane/zero"
import { getChangelogUrl } from "@productlane/lib/src/browser"
import { getChangelogSlug } from "@productlane/lib/src/portal-slugs"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	AlertDialog,
	Button,
	IconButton,
	Popover,
	Shortcut,
} from "@productlane/ui"
import { Calendar } from "@productlane/ui/src/components/internal/calendar"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"
import { CalendarIcon } from "@productlane/ui/src/icons/project/calendar-icon"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ChangelogVersionRow } from "./version-history-dialog"
import { PortalSlugField } from "@/components/shared/portal-slug-field"
import { PublishChangelogDialog } from "@/components/shared/publish-changelog-dialog/publish-changelog-dialog"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { isQuerySettled } from "@/lib/query-settled"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { getLanguageByCode } from "@/screens/settings/settings-pages/language-selector"
import { shouldRejectKeystrokes } from "../sidebar"
import { useChangelogLanguage } from "./changelog-context"
import { sortChangelogsForNav } from "./changelog-order"
import { ChangelogPortalPicker } from "./changelog-portal-picker"
import { primaryPortalInstanceId } from "./changelog-portals"
import { restoreChangelogVersion } from "./restore-changelog-version"
import { VersionHistoryDialog } from "./version-history-dialog"

interface ChangelogActionsProps {
	changelog: Changelog & { translations?: readonly ChangelogTranslation[] }
	emptyLanguageCodes?: string[]
}

// The overflow menu (unpublish / version history / delete). Split out so it can
// sit at the leftmost position in the header, before the translate button.
export const ChangelogOverflowMenu = memo(
	({ changelog }: { changelog: ChangelogActionsProps["changelog"] }) => {
		const navigate = useNavigate()
		const zero = useZero()
		const workspace = useZeroWorkspace()
		const { selectedLanguage } = useChangelogLanguage()
		const [versions, versionsResult] = useQuery(
			queries.changelogVersions({
				changelogId: changelog.id,
				language: selectedLanguage.code,
			}),
		)

		const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)

		const [changelogs] = useQuery(queries.changelogs())

		const [isDeleteOpen, setIsDeleteOpen] = useState(false)
		const { role } = useRole()

		const handleDelete = useCallback(() => {
			setIsDeleteOpen(true)
			analytics.track("open-delete-changelog-dialog", {
				id: changelog.id,
			})
		}, [changelog.id])

		const handleVersionRestore = useCallback(
			async (version: ChangelogVersionRow) => {
				if (!workspace) return
				const restored = await restoreChangelogVersion({
					zero,
					workspaceId: workspace.id,
					changelogId: changelog.id,
					version,
					defaultLanguage: workspace.defaultLanguage ?? "en",
				})
				if (restored) setIsVersionHistoryOpen(false)
			},
			[zero, workspace, changelog.id],
		)

		const copyChangelogId = async () => {
			await navigator.clipboard.writeText(changelog.id)
			toast.info({ title: "Copied changelog ID to clipboard" })
			analytics.track("copy-changelog-id", { id: changelog.id })
		}

		const saveSlug = async (next: string) => {
			const result = zero.mutate(
				mutators.changelogs.setSlug({ id: changelog.id, slug: next }),
			)
			try {
				await mutateOk(result)
				await mutateOk(result, "server")
			} catch (error) {
				toast.info({
					icon: LinkIcon,
					title:
						error instanceof Error ? error.message : "That URL is not valid",
					description: "Choose a different URL.",
				})
				return
			}
			toast.success({
				icon: LinkIcon,
				title: changelog.slug
					? "URL changed. The old URL redirects."
					: "URL set.",
			})
			analytics.track("set-changelog-url", { id: changelog.id })
		}

		const copyChangelogUrl = async () => {
			if (!workspace) return
			const portalSlug =
				(workspace.portalInstances ?? []).find(
					(instance) => instance.id === primaryPortalInstanceId(changelog),
				)?.slug ?? null
			await navigator.clipboard.writeText(
				getChangelogUrl(workspace, changelog.id, portalSlug),
			)
			toast.info({ title: "Copied changelog URL to clipboard" })
			analytics.track("copy-changelog-url", { id: changelog.id })
		}

		// Add Cmd+Delete keyboard shortcut
		useShortcut("meta+backspace", handleDelete, {
			enabled: role !== "VIEWER",
			enableOnContentEditable: true,
			enableOnFormTags: true,
			skipDefaultGuards: true,
			ignoreEventWhen: () => shouldRejectKeystrokes() ?? false,
		})

		useShortcut("mod+period", () => void copyChangelogId(), {
			preventDefault: true,
		})

		useShortcut("meta+shift+,", () => void copyChangelogUrl(), {
			enableOnContentEditable: false,
			// default splitKey is "," which would break a comma hotkey
			splitKey: "~",
		})

		if (role === "VIEWER") return null

		return (
			<>
				<AlertDialog
					isOpen={isDeleteOpen}
					onOpenChange={setIsDeleteOpen}
					confirmText="Delete"
					title={
						changelog.title
							? `Delete "${changelog.title}"?`
							: "Delete this changelog?"
					}
					description={
						changelog.title
							? `Are you sure you want to delete the changelog "${changelog.title}"?`
							: "Are you sure you want to delete this changelog?"
					}
					onCancel={() => {
						analytics.track("cancel-delete-changelog", {
							id: changelog.id,
						})
						setIsDeleteOpen(false)
					}}
					onConfirm={async () => {
						await mutateOk(
							zero.mutate(
								mutators.changelogs.delete({
									id: changelog.id,
								}),
							),
						)
						analytics.track("delete-changelog", { id: changelog.id })
						setIsDeleteOpen(false)

						const sortedChangelogs = sortChangelogsForNav(changelogs)

						// Navigate to the next changelog in sorted order
						const currentIndex = sortedChangelogs.findIndex(
							(c) => c.id === changelog.id,
						)
						const nextChangelog =
							sortedChangelogs[currentIndex + 1] ||
							sortedChangelogs[currentIndex - 1]

						if (nextChangelog) {
							void navigate({
								to: "/edit-changelog/$id",
								params: { id: nextChangelog.id },
							})
						} else {
							void navigate({ to: "/edit-changelog" })
						}
					}}
				/>

				<VersionHistoryDialog
					versions={versions}
					isLoading={!isQuerySettled(versionsResult)}
					changelogTitle={changelog.title}
					coverRows={[changelog, ...(changelog.translations ?? [])]}
					isOpen={isVersionHistoryOpen}
					onOpenChange={setIsVersionHistoryOpen}
					onRestore={handleVersionRestore}
				/>

				<DropdownMenu
					trigger={
						<IconButton
							Icon={MoreVertical}
							variant="ghost"
							iconClassName="fill-current"
						/>
					}
					contentProps={{ side: "bottom", align: "start" }}
				>
					<DropdownMenuGroup>
						{changelog.published && (
							<DropdownMenuItem
								Icon={EyeOff}
								onClick={async () => {
									await mutateOk(
										zero.mutate(
											mutators.changelogs.update({
												id: changelog.id,
												published: false,
											}),
										),
									)
									toast.success({
										title: "Changelog unpublished",
									})
									analytics.track("changelog-unpublish", {
										id: changelog.id,
									})
								}}
							>
								Unpublish
							</DropdownMenuItem>
						)}
						<DropdownMenuItem
							Icon={History}
							onClick={() => {
								setIsVersionHistoryOpen(true)
								analytics.track("open-version-history", {
									id: changelog.id,
								})
							}}
						>
							Version History
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							Icon={SquareAsterisk}
							onClick={async () => {
								await copyChangelogId()
							}}
							className="flex w-full"
						>
							<div className="flex w-full items-center justify-between">
								<div>Copy ID</div>
								<div className="flex gap-1">
									<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
									<Shortcut>{ShortcutSymbols["."]}</Shortcut>
								</div>
							</div>
						</DropdownMenuItem>
						<DropdownMenuItem
							Icon={LinkIcon}
							onClick={async () => {
								await copyChangelogUrl()
							}}
							className="flex w-full"
						>
							<div className="flex w-full items-center justify-between">
								<div>Copy URL</div>
								<div className="flex gap-1">
									<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
									<Shortcut>{ShortcutSymbols.SHIFT}</Shortcut>
									<Shortcut>{ShortcutSymbols[","]}</Shortcut>
								</div>
							</div>
						</DropdownMenuItem>
						<PortalSlugField
							prefix="/changelog/"
							value={
								changelog.slug ??
								getChangelogSlug(
									{ ...changelog, slug: null },
									workspace?.timezone,
								)
							}
							isFrozen={!!changelog.slug}
							derived={getChangelogSlug(
								{ ...changelog, slug: null },
								workspace?.timezone,
							)}
							onSave={saveSlug}
						/>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							Icon={Trash}
							onClick={handleDelete}
							data-testid="delete-changelog-button"
							className="flex w-full"
						>
							<div className="flex w-full items-center justify-between">
								<div>Delete</div>
								<div className="flex gap-1">
									<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
									<Shortcut>{ShortcutSymbols.BACKSPACE}</Shortcut>
								</div>
							</div>
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenu>
			</>
		)
	},
)

export const ChangelogActions = memo(
	({ changelog, emptyLanguageCodes }: ChangelogActionsProps) => {
		const zero = useZero()

		const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
		const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)
		const [isTranslationWarningOpen, setIsTranslationWarningOpen] =
			useState(false)

		const handlePublishClick = useCallback(() => {
			if (emptyLanguageCodes && emptyLanguageCodes.length > 0) {
				setIsTranslationWarningOpen(true)
			} else {
				setIsPublishModalOpen(true)
			}
		}, [emptyLanguageCodes])

		const handleDateChange = async (newDate: Date | undefined) => {
			if (newDate) {
				await mutateOk(
					zero.mutate(
						mutators.changelogs.update({
							id: changelog.id,
							date: newDate.getTime(),
						}),
					),
				)
				analytics.track("update-changelog-date", { id: changelog.id })
			}
			setIsDatePickerOpen(false)
		}

		return (
			<div className="flex items-center gap-2">
				<RoleCheck not={["VIEWER"]}>
					<Popover
						open={isDatePickerOpen}
						onOpenChange={setIsDatePickerOpen}
						content={
							<Calendar
								mode="single"
								selected={changelog.date ? new Date(changelog.date) : undefined}
								onSelect={handleDateChange}
								autoFocus
								required
							/>
						}
						className="w-auto bg-white p-0 dark:bg-gray-800"
						align="center"
					>
						<Button
							variant="outline"
							className="justify-start text-left font-normal"
							Icon={CalendarIcon}
						>
							{changelog.date
								? format(new Date(changelog.date), "PPP")
								: "Pick a date"}
						</Button>
					</Popover>
				</RoleCheck>

				<ChangelogPortalPicker changelog={changelog} />

				<RoleCheck not={["VIEWER"]}>
					{!changelog.published && (
						<>
							<Button onClick={handlePublishClick}>Publish</Button>
							<AlertDialog
								isOpen={isTranslationWarningOpen}
								onOpenChange={setIsTranslationWarningOpen}
								confirmText="Publish anyway"
								title="Incomplete translations"
								description={`The following languages have empty content: ${emptyLanguageCodes?.map((code) => getLanguageByCode(code)?.name ?? code).join(", ")}. Do you want to publish anyway?`}
								onCancel={() => setIsTranslationWarningOpen(false)}
								onConfirm={() => {
									setIsTranslationWarningOpen(false)
									setIsPublishModalOpen(true)
								}}
							/>
							<PublishChangelogDialog
								isOpen={isPublishModalOpen}
								onOpenChange={setIsPublishModalOpen}
								entity={{
									id: changelog.id,
									name: changelog.title || "New changelog",
									type: "changelog",
									portalInstanceId: primaryPortalInstanceId(changelog),
								}}
							/>
						</>
					)}
				</RoleCheck>
			</div>
		)
	},
)
